// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/designation.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { designationsApi } from "../../api/designations";
import type { Designation } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import { ProgressBar } from "../../components/composition/ProgressBar";
import type { DesignationStatus, DesignationType } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<DesignationStatus, string> = {
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  archived: "Archived",
};
const TYPE_LABEL: Record<DesignationType, string> = {
  fund: "Fund",
  project: "Project",
  program: "Program",
  cause: "Cause",
  milestone: "Milestone",
};

function dashSpan(): ReturnType<typeof createElement> {
  return createElement("span", { style: { color: "var(--color-text-muted)" } }, "—");
}
// override-block:helpers END

export const designationDetailConfig: DetailPageConfig<Designation> = {
  idParam: "designationId",
  queryKey: (id: string) => ["designations", "detail", id] as const,
  queryFn: (id: string) => designationsApi.getDesignation(id),
  backTo: "/designations",
  // override-block:title BEGIN
  title: (r) => r.name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => TYPE_LABEL[r.designation_type] ?? r.designation_type,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-raised BEGIN
    {
      id: "raised",
      label: "Raised",
      value: r.raised_minor_units,
      format: "currency",
      currency: r.goal_currency ?? "USD",
      tone: "g-giving",
    },
    // override-block:statcard-raised END
    // override-block:statcard-goal BEGIN
    {
      id: "goal",
      label: "Goal",
      value: r.goal_amount_minor_units ?? null,
      format: r.goal_amount_minor_units != null ? "currency" : "text",
      currency: r.goal_currency ?? "USD",
      tone: "g-home",
      render: r.goal_amount_minor_units == null ? dashSpan() : undefined,
    },
    // override-block:statcard-goal END
    // override-block:statcard-progress BEGIN
    {
      id: "progress",
      label: "Progress",
      value: r.progress_pct ?? null,
      format: r.progress_pct != null ? "percent" : "text",
      tone: "g-mygiving",
      render: r.progress_pct == null ? dashSpan() : undefined,
    },
    // override-block:statcard-progress END
    // override-block:statcard-donations BEGIN
    {
      id: "donations",
      label: "Donations",
      value: r.donation_count ?? null,
      format: r.donation_count != null ? "number" : "text",
      tone: "g-events",
      render: r.donation_count == null ? dashSpan() : undefined,
    },
    // override-block:statcard-donations END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.designation_detail["edit"], "disable") },
    { id: "complete", label: "Mark complete", variant: "primary", permissionGate: toGate(GATE_CATALOG.designation_detail["complete"], "disable") },
    { id: "archive", label: "Archive", variant: "ghost", permissionGate: toGate(GATE_CATALOG.designation_detail["archive"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per designation-detail-v2.cross-screen.yaml. Overview
    // includes conditional ProgressBar (CHG-1236 4th consumer) only when
    // goal_amount_minor_units is set. donations/recurring/campaigns are
    // placeholder bodies citing bridge endpoints until ε CHGs ship them.
    {
      id: "overview",
      label: "Overview",
      render: (d, fmt) => {
        const hasGoal = d.goal_amount_minor_units != null && d.goal_amount_minor_units > 0;
        return createElement(
          "div",
          { style: { display: "flex", flexDirection: "column", gap: 16 } },
          hasGoal
            ? createElement(
                "div",
                { className: "card", style: { padding: 16 } },
                createElement(ProgressBar, {
                  value: d.raised_minor_units,
                  max: d.goal_amount_minor_units ?? 0,
                  tone: "g-giving",
                  label: "Fundraising progress",
                  valueFormatted: `${fmt.formatCurrency(d.raised_minor_units, d.goal_currency ?? "USD")} of ${fmt.formatCurrency(d.goal_amount_minor_units ?? 0, d.goal_currency ?? "USD")}`,
                  height: 12,
                }),
              )
            : null,
          createElement(
            "div",
            { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
            createElement(
              "div",
              null,
              "Type: ",
              TYPE_LABEL[d.designation_type] ?? d.designation_type,
            ),
            createElement(
              "div",
              null,
              "Status: ",
              STATUS_LABEL[d.status] ?? d.status,
            ),
            createElement(
              "div",
              null,
              "Allows recurring: ",
              d.allow_recurring ? "Yes" : "No",
            ),
            createElement(
              "div",
              null,
              "Created: ",
              fmt.formatDate(d.created_at, "long"),
            ),
            d.parent_id
              ? createElement(
                  "div",
                  { style: { color: "var(--color-text-muted)" } },
                  "Parent: ",
                  d.parent_name ?? d.parent_id.slice(0, 8) + "…",
                )
              : null,
            d.designation_type === "program" && d.schedule_label
              ? createElement(
                  "div",
                  { style: { marginTop: 8, color: "var(--color-text-muted)" } },
                  "Schedule: ",
                  d.schedule_label,
                )
              : null,
          ),
        );
      },
    },
    {
      id: "donations",
      label: "Donations",
      render: (d) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Donations targeting this designation — fetched via GET /donations?designation_id=${d.id} ` +
            "(bridge; canonical /designations/{id}/donations endpoint pending). Full UI in ε CHG.",
        ),
    },
    {
      id: "recurring",
      label: "Recurring",
      render: (d) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Recurring donations targeting this designation — fetched via ` +
            `GET /recurring-donations?designation_id=${d.id} (bridge). Full UI in ε CHG.`,
        ),
    },
    {
      id: "campaigns",
      label: "Campaigns",
      render: (d) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Campaigns linked to this designation — fetched via ` +
            `GET /campaigns?designation_id=${d.id} (bridge). Full UI in ε CHG.`,
        ),
    },
    // override-block:custom-tabs END
  ],
};
